import { useRef } from 'react'
import { useLang, type Key } from '../i18n'
import type { OfficeTemplateKind } from '../lib/api'
import { TEMPLATE_FILE_ACCEPT } from '../lib/office-templates'
import { ArtefactTypeGlyph } from './ArtefactTypeIcon'

const LEAD_KEY = { slides: 'otpl_drop_slides', word: 'otpl_drop_word' } as const satisfies Record<OfficeTemplateKind, Key>
const FILE_KEY = { slides: 'corp_tpl_file', word: 'otpl_file_word' } as const satisfies Record<OfficeTemplateKind, Key>

/**
 * Where a bid Office template's file goes, in the Add and Replace dialogs (AB#464): the look of the
 * PowerPoint import dialog's first step. The whole dialog takes the drop (TemplateDialog's
 * `fileDrop`); this shows where (`over`) and which file was chosen, and offers the picker. Only the
 * button opens it, a clickable div being a control with no role and no keyboard path.
 */
export function TemplateDropZone({
  kind,
  file,
  over,
  disabled,
  onFiles,
}: {
  kind: OfficeTemplateKind
  file: File | null
  over: boolean
  disabled: boolean
  onFiles: (files: FileList | null) => void
}) {
  const { t } = useLang()
  const input = useRef<HTMLInputElement>(null)
  return (
    <div className={'img-drop pptx-drop otpl-drop' + (over ? ' on' : '')}>
      <span className="pptx-drop-ico" aria-hidden="true">
        <ArtefactTypeGlyph type={kind === 'slides' ? 'slides' : 'prose'} />
      </span>
      <p className="img-drop-lead" aria-live="polite">
        {file ? file.name : t(LEAD_KEY[kind])}
      </p>
      {!file && <p className="img-drop-or">{t('img_drop_or')}</p>}
      <button type="button" className="mini ghost" disabled={disabled} onClick={() => input.current?.click()}>
        {t('img_upload')}
      </button>
      <input
        ref={input}
        type="file"
        accept={TEMPLATE_FILE_ACCEPT[kind]}
        aria-label={t(FILE_KEY[kind])}
        hidden
        disabled={disabled}
        onChange={(e) => {
          onFiles(e.target.files)
          e.target.value = '' // allow re-picking the same file
        }}
      />
    </div>
  )
}
